import React, { useEffect, useState, useRef } from 'react';
import {
  IonPage,
  IonContent,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonButton,
  IonIcon,
  IonSpinner,
  IonToast,
  IonRouterLink,
} from '@ionic/react';
import { pencil, add } from 'ionicons/icons';
import { useHistory } from 'react-router';
import { GearCard } from '@/components/GearCard';
import { ClubDistanceChart } from '@/components/ClubDistanceChart';
import ClubDetailsModal from '@/components/ClubDetailsModal';
import { api } from '@/utils/api';
import { t } from 'i18next';

interface GolfClub {
  id: number;
  brandId: number;
  brandName?: string;
  modelId: number;
  modelName?: string;
  customModelName?: string;
  type: string;
  loft?: number;
  clubNumber?: number;
  notes?: string;
  inBag: boolean;
  performance?: {
    averageDistance?: number;
  };
}

const MyBag: React.FC = () => {
  const [clubs, setClubs] = useState<GolfClub[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedClubId, setSelectedClubId] = useState<number | undefined>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const selectedClubRef = useRef<HTMLElement | null>(null);
  const history = useHistory();

  const handleClubClick = (clubId: number, element: React.MouseEvent<HTMLElement>) => {
    setSelectedClubId(clubId);
    selectedClubRef.current = element.currentTarget;
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedClubId(undefined);
  };


  const fetchClubs = async () => {
    try {
      setLoading(true);
      const response = await api.get('/golf-gear/my-bag');

      if (!response.data) {
        throw new Error('Failed to fetch clubs');
      }

      setClubs(response.data);
    } catch (err) {
      console.error('Error fetching clubs:', err);
      setError('Failed to load your golf bag. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClubs();
  }, []);


  // Group clubs by type for display
  const groupedClubs = clubs.reduce((acc, club) => {
    const type = club.type.toLowerCase();
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(club);
    return acc;
  }, {} as Record<string, GolfClub[]>);

  // Club types in display order
  const clubTypes = [
    { key: 'driver', label: 'Driver' },
    { key: 'wood', label: 'Woods' },
    { key: 'hybrid', label: 'Hybrids' },
    { key: 'iron', label: 'Irons' },
    { key: 'wedge', label: 'Wedges' },
    { key: 'putter', label: 'Putter' },
  ];

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton text="" defaultHref="/profile" />
          </IonButtons>
          <IonTitle>{t('mybag')}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => history.push('/my-bag/edit')}>
              <IonIcon slot="icon-only" icon={pencil} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        {loading ? (
          <div className="flex justify-center items-center h-full">
            <IonSpinner name="circular" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-full">
            <p className="text-center text-gray-500 mb-4">{error}</p>
            <IonButton onClick={() => window.location.reload()}>{t('retry')}</IonButton>
          </div>
        ) : clubs.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full">
            <p className="text-center text-gray-500 mb-4">Vous n'avez pas encore ajouté de clubs à votre sac.</p>
            <IonButton shape="round" onClick={() => history.push('/my-bag/edit')}>
              Configurer mon sac
            </IonButton>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <ClubDistanceChart clubs={clubs} />
            </div>

            <div className="md:flex grid grid-cols-1 md:flex-wrap gap-2">
              {clubTypes.map((type) => {
                const typeClubs = groupedClubs[type.key] || [];
                if (typeClubs.length === 0) return null;

                return (
                  <React.Fragment key={type.key}>
                    {typeClubs.map((club) => (
                      <GearCard
                        key={club.id}
                        club={club}
                        onClick={(e) => handleClubClick(club.id, e)}
                      />
                    ))}
                  </React.Fragment>
                );
              })}
            </div>
            <div className="mt-6 mb-10 hidden md:block">
              <IonRouterLink routerLink="/my-bag/edit" className="w-full">
                <IonButton expand="block" className="w-full">
                  Modifier le sac
                </IonButton>
              </IonRouterLink>
            </div>
          </>
        )}

        <IonToast
          isOpen={!!error}
          message={error || ''}
          duration={3000}
          onDidDismiss={() => setError(null)}
          position="bottom"
          color="danger"
        />

        <ClubDetailsModal
          clubId={selectedClubId}
          isOpen={isModalOpen}
          onClose={() => {
            handleCloseModal();
          }}
          onUpdate={fetchClubs}
          triggerRef={selectedClubRef}
        />
      </IonContent>
    </IonPage>
  );
};

export default MyBag;
